import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Badge } from '../../../ui/badge';
import { 
  Search, Filter, X, ArrowUp, ArrowDown, 
  CheckCircle2, AlertCircle, XCircle, Eye, FileText, 
  // MapPin, 
  Users, Clock
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';

interface TourApprovalPageProps {
  onNavigate?: (page: string, tourId?: string) => void;
}

interface Tour {
  id: string;
  tourCode: string;
  tourOperatorOffice: string;
  tourName: string;
  tourType: 'Arbaeen' | 'General' | 'Ashara' | 'Urs';
  applicants: number;
  startDate: string;
  endDate: string;
  status: 'pending-approval' | 'request-schedule-change' | 'venue-approved' | 'venue-rescheduled' | 'approved' | 'rejected';
  submittedDate: string;
  activityLog: Array<{
    date: string;
    time: string;
    user: string;
    role: string;
    action: string;
    details: string;
  }>;
}

const mockToursForApproval: Tour[] = [
  {
    id: 'IRQ-2026-0847',
    tourCode: 'IRQ-2026-0847',
    tourOperatorOffice: 'Al-Mahdi Tours & Travels',
    tourName: 'Ashara Mubaraka 1446H - Iraq Ziyarat',
    tourType: 'Arbaeen',
    applicants: 6,
    startDate: '2026-03-12',
    endDate: '2026-03-20',
    status: 'pending-approval',
    submittedDate: '2026-01-28',
    activityLog: [
      {
        date: '2026-01-28',
        time: '14:30',
        user: 'Ali Hassan',
        role: 'Tour Operator',
        action: 'Tour Created',
        details: 'Tour created with 6 applicants and submitted for venue approval'
      },
      {
        date: '2026-01-29',
        time: '09:15',
        user: 'Venue Team',
        role: 'Venue Approval',
        action: 'Venue Approved',
        details: 'All venues approved without changes'
      },
      {
        date: '2026-01-29',
        time: '10:00',
        user: 'System',
        role: 'Automated',
        action: 'Status Update',
        details: 'Tour moved to approval queue after venue approval'
      }
    ]
  },
  {
    id: 'IRQ-2026-0848',
    tourCode: 'IRQ-2026-0848',
    tourOperatorOffice: 'Hussaini Travel Services',
    tourName: 'Ziyarat Package - Najaf & Karbala',
    tourType: 'General',
    applicants: 12,
    startDate: '2026-03-15',
    endDate: '2026-03-22',
    status: 'request-schedule-change',
    submittedDate: '2026-01-30',
    activityLog: [
      {
        date: '2026-01-30',
        time: '11:20',
        user: 'Fatima Ahmed',
        role: 'Tour Operator',
        action: 'Tour Created',
        details: 'General tour package created with 12 applicants'
      },
      {
        date: '2026-01-31',
        time: '14:45',
        user: 'Venue Team',
        role: 'Venue Approval',
        action: 'Date Changed',
        details: 'Venue availability issue - dates adjusted by 2 days'
      },
      {
        date: '2026-02-01',
        time: '10:30',
        user: 'Fatima Ahmed',
        role: 'Tour Operator',
        action: 'Tour Updated',
        details: 'Tour operator confirmed new dates and resubmitted'
      }
    ]
  },
  {
    id: 'IRQ-2026-0849',
    tourCode: 'IRQ-2026-0849',
    tourOperatorOffice: 'Zainab Travel Agency',
    tourName: 'Iraq Pilgrimage Tour',
    tourType: 'General',
    applicants: 8,
    startDate: '2026-03-18',
    endDate: '2026-03-25',
    status: 'pending-approval',
    submittedDate: '2026-02-02',
    activityLog: []
  },
  {
    id: 'IRQ-2026-0850',
    tourCode: 'IRQ-2026-0850',
    tourOperatorOffice: 'Al-Mahdi Tours & Travels',
    tourName: 'Special Ziyarat - 10 Days',
    tourType: 'Ashara',
    applicants: 15,
    startDate: '2026-03-20',
    endDate: '2026-03-30',
    status: 'pending-approval',
    submittedDate: '2026-02-05',
    activityLog: []
  },
];

const statusColors = {
  'pending-approval': 'bg-orange-100 text-orange-700 border-orange-300',
  'request-schedule-change': 'bg-amber-100 text-amber-700 border-amber-300',
  'venue-approved': 'bg-green-100 text-green-700 border-green-300',
  'venue-rescheduled': 'bg-amber-100 text-amber-700 border-amber-300',
  approved: 'bg-green-100 text-green-700 border-green-300',
  rejected: 'bg-red-100 text-red-700 border-red-300'
};

const statusLabels = {
  'pending-approval': 'Pending Approval',
  'request-schedule-change': 'Request Schedule Change',
  'venue-approved': 'Venue Approved',
  'venue-rescheduled': 'Venue Rescheduled',
  approved: 'Approved',
  rejected: 'Rejected'
};

export default function TourApprovalPage({ onNavigate }: TourApprovalPageProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [selectedTourLog, setSelectedTourLog] = useState<Tour | null>(null);
  
  const [filters, setFilters] = useState({
    tourCode: '',
    operator: '',
    tourType: 'all',
    status: 'all',
  });

  const filteredTours = mockToursForApproval.filter(tour => {
    const matchesSearch = 
      tour.tourCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tour.tourName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tour.tourOperatorOffice.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTourCode = !filters.tourCode || tour.tourCode.toLowerCase().includes(filters.tourCode.toLowerCase());
    const matchesOperator = !filters.operator || tour.tourOperatorOffice.toLowerCase().includes(filters.operator.toLowerCase());
    const matchesTourType = filters.tourType === 'all' || tour.tourType === filters.tourType;
    const matchesStatus = filters.status === 'all' || tour.status === filters.status;
    
    return matchesSearch && matchesTourCode && matchesOperator && matchesTourType && matchesStatus;
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysBetween = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = endDate.getTime() - startDate.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const stats = {
    total: mockToursForApproval.length,
    approved: mockToursForApproval.filter(t => t.status === 'approved').length,
    pending: mockToursForApproval.filter(t => t.status === 'pending-approval').length,
    rejected: mockToursForApproval.filter(t => t.status === 'rejected').length,
    totalApplicants: mockToursForApproval.reduce((sum, t) => sum + t.applicants, 0)
  };

  const handleViewTour = (tourId: string) => {
    if (onNavigate) {
      // onNavigate('tour-approval-view', tourId);
      onNavigate(`tour-approval-view/${tourId}`);
    }
  };

  const handleViewActivityLog = (tour: Tour) => {
    setSelectedTourLog(tour);
    setShowActivityLog(true);
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 style={{ color: '#5B9BD5' }} className="flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Tour Approval
          </h1>
          <p className="text-gray-600 mt-1">
            Review and approve tour submissions from tour operators
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowStatistics(!showStatistics)}
            className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
          >
            {showStatistics ? <ArrowUp className="w-4 h-4 mr-2" /> : <ArrowDown className="w-4 h-4 mr-2" />}
            Statistics
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {showStatistics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total Tours</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Approved</p>
                  <p className="text-2xl font-bold text-green-900">{stats.approved}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">Pending</p>
                  <p className="text-2xl font-bold text-orange-900">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Rejected</p>
                  <p className="text-2xl font-bold text-red-900">{stats.rejected}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Total Applicants</p>
                  <p className="text-2xl font-bold text-purple-900">{stats.totalApplicants}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filter Bar */}
      <Card className="border-[#5B9BD5]/30">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by tour code, name, or tour operator..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#5B9BD5]/30 focus-visible:ring-[#5B9BD5]/30"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
            >
              <Filter className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters */}
      {showFilters && (
        <Card className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2 text-lg">
                  <Filter className="w-5 h-5" />
                  Advanced Filters
                </CardTitle>
                <CardDescription className="mt-1">
                  Filter tours by multiple criteria
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Tour Code</Label>
                <Input
                  placeholder="Search by tour code..."
                  value={filters.tourCode}
                  onChange={(e) => setFilters({ ...filters, tourCode: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Tour Operator</Label>
                <Input
                  placeholder="Search by operator..."
                  value={filters.operator}
                  onChange={(e) => setFilters({ ...filters, operator: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Tour Type</Label>
                <Select value={filters.tourType} onValueChange={(value) => setFilters({ ...filters, tourType: value })}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="Arbaeen">Arbaeen</SelectItem>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Ashara">Ashara</SelectItem>
                    <SelectItem value="Urs">Urs</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Status</Label>
                <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending-approval">Pending Approval</SelectItem>
                    <SelectItem value="request-schedule-change">Request Schedule Change</SelectItem>
                    <SelectItem value="venue-approved">Venue Approved</SelectItem>
                    <SelectItem value="venue-rescheduled">Venue Rescheduled</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters Summary */}
            {(filters.tourCode || filters.operator || filters.tourType !== 'all' || filters.status !== 'all') && (
              <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#5B9BD5] mb-2">Active Filters:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {filters.tourCode && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Code: {filters.tourCode}
                      </Badge>
                    )}
                    {filters.operator && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Operator: {filters.operator}
                      </Badge>
                    )}
                    {filters.tourType !== 'all' && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Type: {filters.tourType}
                      </Badge>
                    )}
                    {filters.status !== 'all' && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Status: {statusLabels[filters.status as keyof typeof statusLabels]}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFilters({
                      tourCode: '',
                      operator: '',
                      tourType: 'all',
                      status: 'all',
                    });
                  }}
                  className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tour List Table */}
      <Card className="border-[#5B9BD5]/30 bg-white">
        <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50 rounded-t-xl">
          <CardTitle style={{ color: '#5B9BD5' }}>Tours Pending Approval</CardTitle>
          <CardDescription>
            {filteredTours.length} tour{filteredTours.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: '#E3F2FD' }}>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">
                    Tour
                  </TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">
                    Name
                  </TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">
                    Applicants
                  </TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">
                    Dates
                  </TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">
                    Duration
                  </TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">
                    Status
                  </TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTours.map((tour) => (
                  <TableRow key={tour.id} className="hover:bg-sky-50/30">
                    <TableCell className="px-4 py-3">
                      <div className="space-y-0.5">
                        <div className="font-mono font-semibold text-sm" style={{ color: '#5B9BD5' }}>
                          {tour.tourCode}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {tour.tourOperatorOffice}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="space-y-0.5">
                        <div className="text-sm font-medium text-foreground">
                          {tour.tourName}
                        </div>
                        <Badge variant="outline" className={
                          tour.tourType === 'Arbaeen' 
                            ? 'bg-purple-100 text-purple-700 border-purple-300 text-xs' 
                            : 'bg-blue-100 text-blue-700 border-blue-300 text-xs'
                        }>
                          {tour.tourType}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3 text-gray-400" />
                        <span className="font-semibold">{tour.applicants}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="space-y-0.5">
                        <div className="font-medium text-foreground text-sm">
                          {formatDate(tour.startDate)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          to {formatDate(tour.endDate)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className="text-sm">{getDaysBetween(tour.startDate, tour.endDate)}D</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <Badge variant="outline" className={statusColors[tour.status]}>
                        {statusLabels[tour.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleViewTour(tour.id)}
                          className="h-8 w-8 hover:bg-sky-50 text-[#5B9BD5]"
                          title="Review Tour"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleViewActivityLog(tour)}
                          className="h-8 w-8 hover:bg-sky-50 text-[#5B9BD5]"
                          title="Activity Log"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {filteredTours.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No tours found</p>
              <p className="text-sm text-gray-400">Try adjusting your search or filters</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Log Modal */}
      <Dialog open={showActivityLog} onOpenChange={setShowActivityLog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#5B9BD5' }}>
              <FileText className="w-5 h-5" />
              Activity Log - {selectedTourLog?.tourCode}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedTourLog && selectedTourLog.activityLog.length > 0 ? (
              selectedTourLog.activityLog.map((log, index) => (
                <Card key={index} className="border-l-4" style={{ borderLeftColor: '#5B9BD5' }}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">
                            {log.role}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {log.date} at {log.time}
                          </span>
                        </div>
                        <h4 className="font-semibold text-sm text-gray-900 mb-1">
                          {log.action}
                        </h4>
                        <p className="text-sm text-gray-600 mb-2">{log.details}</p>
                        <p className="text-xs text-gray-500">
                          <strong>User:</strong> {log.user}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No activity log available for this tour</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}